import React, { useEffect, useMemo, useState } from 'react';
import { Button, FormInput } from 'components/common';
import ReactDOM from 'react-dom';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { MdOutlineClose, MdOutlineSave, MdInfoOutline, MdAdd } from 'react-icons/md';
import { FaTrash } from 'react-icons/fa';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { updateSalesForecast } from 'redux/crm/releaseForecastSlice';
import { WEIGHT_INPUT_STEP } from '../components/helpers';
import { Link } from 'react-router-dom';

dayjs.extend(customParseFormat);

// ── helpers ──────────────────────────────────────────────────────────────────

/**
 * Given a period string like "Mar-2026", returns labels for the 3 forecast months.
 * e.g. ["Apr-26", "May-26", "Jun-26"]
 */
const getForecastMonthLabels = (period) => {
  if (!period) return ['Month 1', 'Month 2', 'Month 3'];
  const base = dayjs(period, 'MMM-YYYY');
  if (!base.isValid()) return ['Month 1', 'Month 2', 'Month 3'];
  return [1, 2, 3].map((i) => base.add(i, 'month').format('MMM-YYYY'));
};

const MONTH_OPTIONS = [
  { value: '', label: '— Unassigned —' },
  { value: '1', label: 'Month 1' },
  { value: '2', label: 'Month 2' },
  { value: '3', label: 'Month 3' },
];

// ── component ─────────────────────────────────────────────────────────────────

const SalesForecastEditForm = ({ show, onHide, record, onSaved }) => {
  const dispatch = useDispatch();

  const monthLabels = useMemo(
    () => getForecastMonthLabels(record?.period),
    [record]
  );

  const defaultValues = useMemo(() => {
    const a = record || {};
    return {
      tentative_date_of_rad: a.tentative_date_of_rad?.slice(0, 10) || '',
      tentative_date_of_contract: a.tentative_date_of_contract?.slice(0, 10) || '',
      tentative_date_of_contract_note: a.tentative_date_of_contract_note || '',
      percent_chance_of_winning: a.percent_chance_of_winning ?? '',
      expected_date_release_fabrication:
        a.expected_date_release_fabrication?.slice(0, 10) || '',
      percent_change_of_release: a.percent_change_of_release ?? '',
      remark: a.remark || '',
      buildings: (a.buildings || []).map((b) => ({
        id: b.id,
        name: b.name,
        weight: b.weight ?? '',
        remark: b.remark || '',
        month: b.month != null ? String(b.month) : '',
      })),
    };
  }, [record]);

  const [bulkMonth, setBulkMonth] = useState('');

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isSubmitting, errors },
  } = useForm({ 
    defaultValues,
    mode: 'onChange',
  });

  const { fields, append, remove: removeRow } = useFieldArray({ control, name: 'buildings' });

  // Month totals (live)
  const buildings = watch('buildings');
  const monthTotals = useMemo(() => {
    const totals = { 1: 0, 2: 0, 3: 0 };
    buildings?.forEach((b) => {
      const m = parseInt(b.month, 10);
      if (m >= 1 && m <= 3) {
        totals[m] += parseFloat(b.weight) || 0;
      }
    });
    return totals;
  }, [buildings]);

  const applyBulkMonth = () => {
    if (!bulkMonth) return;
    fields.forEach((_, idx) => {
      setValue(`buildings.${idx}.month`, bulkMonth, { shouldDirty: true });
    });
  };

  const handleDeleteRow = (index) => {
    const row = fields[index];
    const count = fields.filter((f) => f.name === row.name).length;
    if (count <= 1) {
      toast.error('Cannot remove the last month entry for a building');
      return;
    }
    removeRow(index);
  };

  const handleAddSplit = (index) => {
    const row = fields[index];
    const usedMonths = (buildings || [])
      .filter((m) => m.name === row.name)
      .map((m) => m.month);
    const next = MONTH_OPTIONS.filter((m) => m.value).find(
      (m) => !usedMonths.includes(m.value)
    );
    if (!next) {
      toast.info('All months already assigned for this building');
      return;
    }
    append({
      id: row.id,
      name: row.name,
      weight: '',
      remark: '',
      month: next.value,
    });
  };

  // Reset form when the record changes
  useEffect(() => {
    reset(defaultValues);
  }, [record, reset, defaultValues]);

  // Lock body scroll while open
  useEffect(() => {
    document.body.style.overflow = show ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [show]);

  const onSubmit = async (data) => {
    const payload = {
      ...data,
      buildings: data.buildings.map((b) => {
        const monthIdx = b.month !== '' ? parseInt(b.month, 10) : null;
        return {
          id: b.id,
          name: b.name,
          weight: parseFloat(b.weight) || 0,
          remark: b.remark,
          month: monthIdx,
          month_label: monthIdx != null ? monthLabels[monthIdx - 1] : null,
        };
      }),
    };

    try {
      await dispatch(
        updateSalesForecast({
          saleId: record?.id,
          data: payload,
        })
      ).unwrap();
      onSaved?.();
      onHide();
    } catch (error) {
      toast.error(error?.message || 'Failed to save forecast');
    }
  };

  const project = record?.project || {};
  const isLocked = !!record?.sent_at;

  if (!show) return null;

  // Portal — escapes sidebar/header stacking contexts; zIndex via inline style
  return ReactDOM.createPortal(
    <div
      className="tw-fixed tw-inset-0 tw-flex tw-justify-end"
      style={{ zIndex: 99999 }}
    >
      {/* Backdrop */}
      <div
        className="tw-absolute tw-inset-0 tw-bg-black tw-bg-opacity-50"
      />

      {/* Panel — sits on top of backdrop */}
      <div
        className="tw-relative tw-bg-white tw-h-full tw-shadow-2xl tw-flex tw-flex-col"
        style={{ width: '60%', minWidth: 480, maxWidth: 900 }}
      >
        {/* Header */}
        <div className="tw-px-6 tw-py-4 tw-bg-gray-100 tw-border-b tw-border-gray-200 tw-flex tw-items-center tw-justify-between tw-flex-shrink-0">
          <div className="tw-flex tw-items-center tw-gap-3">
            <h5 className="tw-font-semibold tw-text-base tw-mb-0">
              Sales Release Forecast
            </h5>
            {isLocked && (
              <span className="tw-inline-block tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-bg-green-100 tw-text-green-800 tw-rounded-sm">
                Sent to CSD
              </span>
            )}
          </div>
          <Button
            onClick={onHide}
            variant="secondary"
            aria-label="Close"
          >
            ✕
          </Button>
        </div>

        {/* Body - Scrollable */}
        <div className="tw-flex-1 tw-overflow-y-auto tw-p-6">
          {/* Project Info */}
          <div className="tw-bg-gray-100 tw-rounded tw-p-4 tw-mb-6">
            <div className="tw-grid tw-grid-cols-2 tw-gap-4 tw-mb-4">
              <div>
                <div className="tw-text-gray-600 tw-text-sm">Project</div>
                <div className="tw-font-semibold tw-text-blue-400">
                  <Link to={`/crm/projects/${project.id}`} target="_blank" className="tw-hover:tw-no-underline">
                    {project.name || '—'}
                  </Link>
                </div>
              </div>
              <div>
                <div className="tw-text-gray-600 tw-text-sm">Quote/Job Number</div>
                <div className="tw-font-semibold tw-text-blue-400">{project.j_number || project.q_number || '—'}</div>
              </div>
              <div>
                <div className="tw-text-gray-600 tw-text-sm">Period</div>
                <div className="tw-font-semibold tw-text-blue-400">{record?.period || '—'}</div>
              </div>
              <div>
                <div className="tw-text-gray-600 tw-text-sm">Customer</div>
                <div className="tw-font-semibold tw-text-blue-400">
                  <Link to={`/crm/customers/${project.customer_id}`} target="_blank" className="tw-hover:tw-no-underline">
                    {project.customer_name || '—'}
                  </Link>
                </div>
              </div>
            </div>
            <div>
              <div className="tw-text-gray-600 tw-text-sm">Sales Incharge</div>
              <div className="tw-font-semibold tw-text-blue-400">{project.sales_incharge || '—'}</div>
            </div>
          </div>

          {/* Form Content */}
          <form id="salesForecastForm" onSubmit={handleSubmit(onSubmit)}>
            {/* Forecast Parameters */}
            <div className="tw-mb-6">
              <h6 className="tw-text-sm tw-font-bold tw-text-gray-600 tw-uppercase tw-mb-4">
                Forecast Parameters
              </h6>
              <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                {/* LEFT COLUMN */}
                <div className="tw-space-y-4">
                  {/* Tentative Date of RAD */}
                  <div>
                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
                      Tentative Date of RAD
                      <span className="tw-text-red-500 tw-ml-1">*</span>
                    </label>
                    <Controller
                      control={control}
                      name="tentative_date_of_rad"
                      rules={{
                        required: 'Tentative Date of RAD is required',
                      }}
                      render={({ field: f }) => (
                        <div>
                          <DatePicker
                            selected={f.value ? dayjs(f.value).toDate() : null}
                            onChange={(date) => f.onChange(date ? dayjs(date).format('YYYY-MM-DD') : '')}
                            dateFormat="dd-MMM-yyyy"
                            placeholderText="Select date…"
                            disabled={isLocked}
                            className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-md tw-text-sm disabled:tw-bg-gray-100 ${
                              errors.tentative_date_of_rad
                                ? 'tw-border-red-500'
                                : 'tw-border-gray-300'
                            }`}
                          />
                          {errors.tentative_date_of_rad && (
                            <p className="tw-text-red-600 tw-text-xs tw-mt-1">
                              {errors.tentative_date_of_rad.message}
                            </p>
                          )}
                        </div>
                      )}
                    />
                  </div>

                  {/* Tentative Date of Contract */}
                  <div>
                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
                      Tentative Date of Contract
                    </label>
                    <Controller
                      control={control}
                      name="tentative_date_of_contract"
                      rules={{
                        validate: (value) => {
                          const noteValue = watch('tentative_date_of_contract_note');
                          if (!value && !noteValue) {
                            return 'Either Tentative Date of Contract or Contract note is required';
                          }
                          return true;
                        },
                      }}
                      render={({ field: f }) => (
                        <div>
                          <DatePicker
                            selected={f.value ? dayjs(f.value).toDate() : null}
                            onChange={(date) => {
                              f.onChange(date ? dayjs(date).format('YYYY-MM-DD') : '');
                              // A contract date means the deal is won. The model
                              // forces this on save too (Sale#mark_as_won_when_contract_dated);
                              // setting it here keeps the form showing what will be stored.
                              if (date) {
                                setValue('percent_chance_of_winning', 100, {
                                  shouldDirty: true,
                                  shouldValidate: true,
                                });
                              }
                            }}
                            dateFormat="dd-MMM-yyyy"
                            placeholderText="Select date…"
                            disabled={isLocked}
                            className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-md tw-text-sm disabled:tw-bg-gray-100 ${
                              errors.tentative_date_of_contract
                                ? 'tw-border-red-500'
                                : 'tw-border-gray-300'
                            }`}
                          />
                          {errors.tentative_date_of_contract && (
                            <p className="tw-text-red-600 tw-text-xs tw-mt-1">
                              {errors.tentative_date_of_contract.message}
                            </p>
                          )}
                        </div>
                      )}
                    />
                  </div>

                  {/* Tentative of Contract note */}
                  <div>
                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
                      Tentative of Contract note
                    </label>
                    <textarea
                      rows={4}
                      placeholder="Enter any contract note"
                      disabled={isLocked}
                      className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-md tw-text-sm disabled:tw-bg-gray-100 ${
                        errors.tentative_date_of_contract_note
                          ? 'tw-border-red-500'
                          : 'tw-border-gray-300'
                      }`}
                      {...register('tentative_date_of_contract_note', {
                        validate: (value) => {
                          const dateValue = watch('tentative_date_of_contract');
                          if (!value && !dateValue) {
                            return 'Either Tentative Date of Contract or Contract note is required';
                          }
                          return true;
                        },
                      })}
                    />
                    {errors.tentative_date_of_contract_note && (
                      <p className="tw-text-red-600 tw-text-xs tw-mt-1">
                        {errors.tentative_date_of_contract_note.message}
                      </p>
                    )}
                  </div>
                </div>

                {/* RIGHT COLUMN */}
                <div className="tw-space-y-4">
                  {/* % Chance of Winning */}
                  <div>
                    <Controller
                      control={control}
                      name="percent_chance_of_winning"
                      rules={{ required: '% Chance of Winning is required', min: { value: 0, message: 'Min 0' }, max: { value: 100, message: 'Max 100' } }}
                      render={({ field: f }) => (
                        <div>
                          <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
                            % Chance of Winning <span className="tw-text-red-500">*</span>
                          </label>
                          <input
                            {...f}
                            type="number"
                            min={0}
                            max={100}
                            step={1}
                            placeholder="0-100"
                            disabled={isLocked}
                            className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-md tw-text-sm disabled:tw-bg-gray-100 ${errors.percent_chance_of_winning ? 'tw-border-red-500' : 'tw-border-gray-300'}`}
                          />
                          {errors.percent_chance_of_winning && (
                            <p className="tw-text-red-600 tw-text-xs tw-mt-1">{errors.percent_chance_of_winning.message}</p>
                          )}
                        </div>
                      )}
                    />
                  </div>

                  {/* % Chance of Release */}
                  <div>
                    <Controller
                      control={control}
                      name="percent_change_of_release"
                      rules={{ required: '% Chance of Release is required', min: { value: 0, message: 'Min 0' }, max: { value: 100, message: 'Max 100' } }}
                      render={({ field: f }) => (
                        <div>
                          <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
                            % Chance of Release <span className="tw-text-red-500">*</span>
                          </label>
                          <input
                            {...f}
                            type="number"
                            min={0}
                            max={100}
                            step={1}
                            placeholder="0-100"
                            disabled={isLocked}
                            className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-md tw-text-sm disabled:tw-bg-gray-100 ${errors.percent_change_of_release ? 'tw-border-red-500' : 'tw-border-gray-300'}`}
                          />
                          {errors.percent_change_of_release && (
                            <p className="tw-text-red-600 tw-text-xs tw-mt-1">{errors.percent_change_of_release.message}</p>
                          )}
                        </div>
                      )}
                    />
                  </div>

                  {/* Expected Date of Release Fabrication */}
                  <div>
                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
                      Expected Date of Release Fabrication
                      <span className="tw-text-red-500 tw-ml-1">*</span>
                    </label>
                    <Controller
                      control={control}
                      name="expected_date_release_fabrication"
                      rules={{
                        required: 'Expected Date of Release Fabrication is required',
                      }}
                      render={({ field: f }) => (
                        <div>
                          <DatePicker
                            selected={f.value ? dayjs(f.value).toDate() : null}
                            onChange={(date) => f.onChange(date ? dayjs(date).format('YYYY-MM-DD') : '')}
                            dateFormat="dd-MMM-yyyy"
                            placeholderText="Select date…"
                            disabled={isLocked}
                            className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-md tw-text-sm disabled:tw-bg-gray-100 ${
                              errors.expected_date_release_fabrication
                                ? 'tw-border-red-500'
                                : 'tw-border-gray-300'
                            }`}
                          />
                          {errors.expected_date_release_fabrication && (
                            <p className="tw-text-red-600 tw-text-xs tw-mt-1">
                              {errors.expected_date_release_fabrication.message}
                            </p>
                          )}
                        </div>
                      )}
                    />
                  </div>
                </div>
              </div>

              {/* REMARK - FULL WIDTH (after the 2-column grid) */}
              <div className="tw-mt-4">
                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">Remark</label>
                <textarea
                  rows={4}
                  placeholder="Enter any remark…"
                  disabled={isLocked}
                  className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-text-sm disabled:tw-bg-gray-100"
                  {...register('remark')}
                />
              </div>
            </div>

            {/* Month Distribution */}
            <div className="tw-mb-6">
              <h6 className="tw-text-sm tw-font-bold tw-text-gray-600 tw-uppercase tw-mb-3">
                Month Distribution
              </h6>
              <div className="tw-flex tw-gap-8 tw-mb-4">
                {[1, 2, 3].map((m) => (
                  <div key={m} className="tw-text-center">
                    <div className="tw-text-gray-600 tw-text-sm">{monthLabels[m - 1]}</div>
                    <div className="tw-font-bold tw-text-blue-600">
                      {monthTotals[m].toFixed(2)} MT
                    </div>
                  </div>
                ))}
              </div>

              {!isLocked && fields.length > 0 && (
                <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3 tw-p-3 tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded">
                  <span className="tw-text-sm tw-text-blue-700 tw-font-medium tw-whitespace-nowrap">
                    Apply to all:
                  </span>
                  <select
                    value={bulkMonth}
                    onChange={(e) => setBulkMonth(e.target.value)}
                    className="tw-px-2 tw-py-1.5 tw-border tw-border-blue-300 tw-rounded-sm tw-text-sm tw-bg-white tw-text-gray-700"
                  >
                    <option value="">— Select month —</option>
                    {MONTH_OPTIONS.filter((o) => o.value).map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {monthLabels[parseInt(opt.value, 10) - 1]}
                      </option>
                    ))}
                  </select>
                  <Button
                    type="button"
                    variant="primary"
                    onClick={applyBulkMonth}
                    disabled={!bulkMonth}
                  >
                    Apply
                  </Button>
                </div>
              )}

              <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-blue-600 tw-mb-3 tw-p-2 tw-bg-blue-50 tw-rounded-sm">
                <MdInfoOutline size={16} className="tw-flex-shrink-0" />
                <span>Weight (M.T) will be generated automatically based on released estimates.</span>
              </div>

              {fields.length > 0 ? (
                <div className="tw-overflow-x-auto tw-border tw-border-gray-200 tw-rounded-sm">
                  <table className="tw-w-full tw-text-sm">
                    <thead className="tw-bg-gray-100 tw-border-b tw-border-gray-200">
                      <tr>
                        <th className="tw-px-3 tw-py-2 tw-text-left">Building</th>
                        <th className="tw-px-3 tw-py-2 tw-text-left" style={{ width: 110 }}>Weight (MT)</th>
                        <th className="tw-px-3 tw-py-2 tw-text-left" style={{ width: 160 }}>Month</th>
                        <th className="tw-px-3 tw-py-2 tw-text-left" style={{ width: 90 }}>Release Month</th>
                        <th className="tw-px-3 tw-py-2 tw-text-left">Remark</th>
                        {!isLocked && <th style={{ width: 80 }}>Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {fields.map((field, idx) => {
                        const currentMonth = buildings?.[idx]?.month;
                        const releaseLabel = currentMonth
                          ? monthLabels[parseInt(currentMonth, 10) - 1]
                          : null;
                        return (
                          <tr
                            key={field.id}
                            className="tw-border-b tw-border-gray-200 hover:tw-bg-gray-50"
                          >
                            <td className="tw-px-3 tw-py-2">{field.name}</td>
                            <td className="tw-px-3 tw-py-2">
                              <Controller
                                control={control}
                                name={`buildings.${idx}.weight`}
                                render={({ field: f }) => (
                                  <FormInput
                                    type="number"
                                    step={WEIGHT_INPUT_STEP}
                                    min={0}
                                    placeholder="MT"
                                    disabled={isLocked}
                                    error={errors.buildings?.[idx]?.weight?.message}
                                    className="tw-px-2 tw-py-1"
                                    {...f}
                                  />
                                )}
                              />
                            </td>
                            <td className="tw-px-3 tw-py-2">
                              <Controller
                                control={control}
                                name={`buildings.${idx}.month`}
                                render={({ field: f }) => (
                                  <select
                                    {...f}
                                    className="tw-w-full tw-px-2 tw-py-1 tw-border tw-border-gray-300 tw-rounded-sm tw-text-sm disabled:tw-bg-gray-100"
                                    disabled={isLocked}
                                  >
                                    {MONTH_OPTIONS.map((opt) => (
                                      <option key={opt.value} value={opt.value}>
                                        {opt.value
                                          ? monthLabels[parseInt(opt.value, 10) - 1]
                                          : opt.label}
                                      </option>
                                    ))}
                                  </select>
                                )}
                              />
                            </td>
                            <td className="tw-px-3 tw-py-2">
                              {releaseLabel ? (
                                <span className="tw-inline-block tw-px-2 tw-py-0.5 tw-rounded-sm tw-text-xs tw-font-semibold tw-bg-blue-100 tw-text-blue-700">
                                  {releaseLabel}
                                </span>
                              ) : (
                                <span className="tw-text-gray-400 tw-text-xs">—</span>
                              )}
                            </td>
                            <td className="tw-px-3 tw-py-2">
                              <Controller
                                control={control}
                                name={`buildings.${idx}.remark`}
                                render={({ field: f }) => (
                                  <FormInput
                                    type="text"
                                    placeholder="Enter remark…"
                                    disabled={isLocked}
                                    error={errors.buildings?.[idx]?.remark?.message}
                                    className="tw-px-2 tw-py-1"
                                    {...f}
                                  />
                                )}
                              />
                            </td>
                            {!isLocked && (
                              <td className="tw-px-2 tw-py-2 tw-text-center tw-flex tw-items-center tw-justify-center tw-gap-2">
                                <button
                                  type="button"
                                  title="Split to another month"
                                  className="tw-border-0 tw-bg-transparent tw-cursor-pointer tw-text-blue-400 hover:tw-text-blue-600 tw-p-1 tw-rounded-sm hover:tw-bg-blue-50"
                                  onClick={() => handleAddSplit(idx)}
                                >
                                  <MdAdd size={14} />
                                </button>
                                <button
                                  type="button"
                                  title="Remove building"
                                  className="tw-border-0 tw-bg-transparent tw-cursor-pointer tw-text-red-400 hover:tw-text-red-600 tw-p-1 tw-rounded-sm hover:tw-bg-red-50"
                                  onClick={() => handleDeleteRow(idx)}
                                >
                                  <FaTrash size={11} />
                                </button>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="tw-text-gray-500 tw-text-sm">No buildings assigned.</p>
              )}
            </div>
          </form>
        </div>

        {/* Footer - Sticky */}
        <div className="tw-px-6 tw-py-4 tw-bg-gray-100 tw-border-t tw-border-gray-200 tw-flex tw-items-center tw-justify-center tw-gap-3 tw-flex-shrink-0">
          {!isLocked && (
            <>
              <Button
                variant="primary"
                type="submit"
                form="salesForecastForm"
                disabled={isSubmitting}
              >
                <MdOutlineSave />
                {isSubmitting ? 'Saving…' : 'Save'}
              </Button>
              <Button
                variant="danger"
                type="button"
                onClick={onHide}
                disabled={isSubmitting}
              >
                <MdOutlineClose />
                Close
              </Button>
            </>
          )}
          {isLocked && (
            <Button
              variant="secondary"
              type="button"
              onClick={onHide}
            >
              <MdOutlineClose />
              Close
            </Button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

SalesForecastEditForm.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  record: PropTypes.object,
  onSaved: PropTypes.func,
};

SalesForecastEditForm.defaultProps = {
  record: null,
  onSaved: null,
};

export default SalesForecastEditForm;
